import { fetchJobStatusesFromApi } from '@/lib/api/jobs/fetchers'
import { isStaging } from '@/modules/firebase'
import type { JobStatusRow } from '@/schemas/jobs/schema'

const STORAGE_KEY = isStaging
    ? 'littaApi:jobStatuses:v2:staging'
    : 'littaApi:jobStatuses:v2'
/** Cache refreshes once per day at this hour, Europe/London. */
const CACHE_REFRESH_HOUR = 5
const CACHE_TIMEZONE = 'Europe/London'

interface JobStatusesCacheEnvelope {
    fetchedAtMs: number
    statuses: JobStatusRow[]
}

type LondonDateParts = {
    year: number
    month: number
    day: number
    hour: number
}

let memoryCache: JobStatusRow[] | null = null
let memoryFetchedAtMs: number | null = null
let inflight: Promise<JobStatusRow[]> | null = null

const readLocalStorageEnvelope = (): JobStatusesCacheEnvelope | null => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY)
        if (!raw) {
            return null
        }
        const parsed = JSON.parse(raw) as JobStatusesCacheEnvelope
        if (
            typeof parsed.fetchedAtMs !== 'number' ||
            !Array.isArray(parsed.statuses)
        ) {
            return null
        }
        return parsed
    } catch {
        return null
    }
}

const getLondonDateParts = (date: Date): LondonDateParts => {
    const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: CACHE_TIMEZONE,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        hour12: false,
    }).formatToParts(date)

    const get = (type: string) =>
        Number(parts.find(part => part.type === type)?.value ?? 0)

    return {
        year: get('year'),
        month: get('month'),
        day: get('day'),
        hour: get('hour'),
    }
}

const formatLondonDayKey = (
    parts: Pick<LondonDateParts, 'year' | 'month' | 'day'>,
) =>
    `${parts.year}-${String(parts.month).padStart(2, '0')}-${String(parts.day).padStart(2, '0')}`

const getPreviousLondonDay = (
    year: number,
    month: number,
    day: number,
    instantMs: number,
): Pick<LondonDateParts, 'year' | 'month' | 'day'> => {
    let probe = instantMs
    for (let index = 0; index < 48; index++) {
        probe -= 60 * 60 * 1000
        const candidate = getLondonDateParts(new Date(probe))
        if (
            candidate.year !== year ||
            candidate.month !== month ||
            candidate.day !== day
        ) {
            return candidate
        }
    }

    return { year, month, day }
}

/** Daily cache period key: 5am London → 5am London. */
const getCachePeriodKey = (instantMs: number): string => {
    const parts = getLondonDateParts(new Date(instantMs))

    if (parts.hour >= CACHE_REFRESH_HOUR) {
        return formatLondonDayKey(parts)
    }

    return formatLondonDayKey(
        getPreviousLondonDay(parts.year, parts.month, parts.day, instantMs),
    )
}

const isFresh = (fetchedAtMs: number): boolean =>
    getCachePeriodKey(fetchedAtMs) === getCachePeriodKey(Date.now())

const writeLocalStorageEnvelope = (statuses: JobStatusRow[]): number => {
    const fetchedAtMs = Date.now()
    const envelope: JobStatusesCacheEnvelope = {
        fetchedAtMs,
        statuses,
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify(envelope))
    return fetchedAtMs
}

const setMemoryCache = (statuses: JobStatusRow[], fetchedAtMs: number) => {
    memoryCache = statuses
    memoryFetchedAtMs = fetchedAtMs
}

const clearMemoryCache = () => {
    memoryCache = null
    memoryFetchedAtMs = null
}

const fetchAndPersist = async (): Promise<JobStatusRow[]> => {
    const staleEnvelope = readLocalStorageEnvelope()

    try {
        const statuses = await fetchJobStatusesFromApi()
        const fetchedAtMs = writeLocalStorageEnvelope(statuses)
        setMemoryCache(statuses, fetchedAtMs)
        return statuses
    } catch (error) {
        if (staleEnvelope) {
            console.warn(
                'GET /jobs/statuses failed — using stale localStorage cache',
                error,
            )
            setMemoryCache(staleEnvelope.statuses, staleEnvelope.fetchedAtMs)
            return staleEnvelope.statuses
        }
        throw error
    }
}

export async function getJobStatusesCached(): Promise<JobStatusRow[]> {
    if (
        memoryCache &&
        memoryFetchedAtMs !== null &&
        isFresh(memoryFetchedAtMs)
    ) {
        return memoryCache
    }

    clearMemoryCache()

    const envelope = readLocalStorageEnvelope()
    if (envelope && isFresh(envelope.fetchedAtMs)) {
        setMemoryCache(envelope.statuses, envelope.fetchedAtMs)
        return envelope.statuses
    }

    if (inflight) {
        return inflight
    }

    inflight = fetchAndPersist().finally(() => {
        inflight = null
    })

    return inflight
}
